import { lazy, Suspense, useEffect, useState } from 'react';
import { InboxScopeProvider, DefaultInboxRedirect } from '@/lib/inbox-scope';
import { Outlet, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { LightboxProvider } from '@/components/Lightbox';
import { AuthProvider, RequireAuth } from '@/lib/auth-guard';
import { WorkspacePresenceProvider } from '@/lib/workspace-presence-context';
import { AssistSessionProvider } from '@/lib/assist-session-context';
import { CustomerAssistWidget } from '@/components/CustomerAssistWidget';
import { TopNav } from '@/components/TopNav';
import { Skeleton } from '@/components/ui/skeleton';
import { CommandPalette } from '@/components/CommandPalette';
import { ShortcutsCheatsheet } from '@/components/ShortcutsCheatsheet';
import { PullToRefresh } from '@/components/PullToRefresh';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useKeyboardShortcuts } from '@/lib/use-keyboard-shortcuts';
import { useDesktopNotifications } from '@/lib/use-desktop-notifications';
import { DesktopNotificationsBanner } from '@/components/DesktopNotificationsBanner';
import { useTabAttention } from '@/lib/use-tab-attention';
import { useNotificationsRealtime } from '@/lib/use-notifications-realtime';
import { usePendingTicketActionsGuard } from '@/lib/use-ticket-action';
import { useSendOutboxToasts } from '@/lib/use-send-outbox';

// Hot-path routes stay eager — dashboard + ticket detail load instantly on
// first paint because they're where the team lives. Login + auth callback
// also eager since they block first render.
import { LoginRoute } from '@/routes/login';
import { AuthCallbackRoute } from '@/routes/auth-callback';
import { DashboardRoute } from '@/routes/dashboard';
import { TicketDetailRoute } from '@/routes/ticket-detail';

// Cold-path routes lazy-load. Each becomes its own chunk that only fetches
// when the user actually navigates to it. Heavy ones (Reports + Journeys
// pull in Recharts; Settings drags the HTML preview pipeline) benefit most.
const MyDayRoute = lazy(() => import('@/routes/my-day').then((m) => ({ default: m.MyDayRoute })));
const ActivityRoute = lazy(() => import('@/routes/activity').then((m) => ({ default: m.ActivityRoute })));
const ContactsRoute = lazy(() => import('@/routes/contacts').then((m) => ({ default: m.ContactsRoute })));
const ContactRoute = lazy(() => import('@/routes/contact').then((m) => ({ default: m.ContactRoute })));
const JourneysRoute = lazy(() => import('@/routes/journeys').then((m) => ({ default: m.JourneysRoute })));
const JourneyPrintRoute = lazy(() => import('@/routes/journey-print').then((m) => ({ default: m.JourneyPrintRoute })));
const KbRoute = lazy(() => import('@/routes/kb').then((m) => ({ default: m.KbRoute })));
const SlaRulesRoute = lazy(() => import('@/routes/sla-rules').then((m) => ({ default: m.SlaRulesRoute })));
const TeamRoute = lazy(() => import('@/routes/team').then((m) => ({ default: m.TeamRoute })));
const TeamsRoute = lazy(() => import('@/routes/teams').then((m) => ({ default: m.TeamsRoute })));
const RolesRoute = lazy(() => import('@/routes/roles').then((m) => ({ default: m.RolesRoute })));
const AccessRoute = lazy(() => import('@/routes/access').then((m) => ({ default: m.AccessRoute })));
const ChangelogRoute = lazy(() => import('@/routes/changelog').then((m) => ({ default: m.ChangelogRoute })));
const NotificationsRoute = lazy(() => import('@/routes/notifications').then((m) => ({ default: m.NotificationsRoute })));
const ReportsRoute = lazy(() => import('@/routes/reports').then((m) => ({ default: m.ReportsRoute })));
const TemplatesRoute = lazy(() => import('@/routes/templates').then((m) => ({ default: m.TemplatesRoute })));
const SettingsRoute = lazy(() => import('@/routes/settings').then((m) => ({ default: m.SettingsRoute })));
const CompanyRoute = lazy(() => import('@/routes/company').then((m) => ({ default: m.CompanyRoute })));
const LeaveRoute = lazy(() => import('@/routes/leave').then((m) => ({ default: m.LeaveRoute })));
const InboxesRoute = lazy(() => import('@/routes/inboxes'));
const AutomationsRoute = lazy(() => import('@/routes/automations').then((m) => ({ default: m.AutomationsRoute })));
const AssistRoute = lazy(() => import('@/routes/assist').then((m) => ({ default: m.AssistRoute })));

// Subtle fade-in on every route change. The `key={pathname}` re-mounts the
// inner subtree on navigation, which restarts the CSS `animate-in fade-in`
// keyframes — Tailwind's tw-animate-css plugin already ships the keyframes
// the design system uses everywhere else (toasts, dialogs). 120ms is short
// enough to feel like Apple's "this is one app, not a series of pages" and
// long enough that the eye registers it as motion rather than a flash.
//
// Cost: a single extra div per render. The Outlet sits inside; React Query
// data cached at the layout level (auth, presence, branding) survives the
// remount because it lives in the QueryClient.
function RouteFadeIn({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="animate-in fade-in duration-150">
      {children}
    </div>
  );
}

// Generic skeleton shown while a lazy chunk is fetching. Tailored to the
// chrome we know is already on screen (TopNav still rendered) so the
// transition feels seamless rather than blank-flashing.
function RouteFallback() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function AuthedLayout() {
  // Used to key the ErrorBoundary below, so navigating away from a route that
  // threw resets it instead of leaving the recovery card up forever.
  const { pathname: layoutPathname } = useLocation();
  // ⌘K palette + ? cheatsheet are mounted once at the layout level so they
  // work on every authed page. Bindings live here too — keeps "open the
  // palette" in one place rather than scattered across routes.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  // Desktop banner + chime bridge for the in-app notifications list.
  // Mounted once here (not per-route) so the "what have I already
  // spawned" set survives navigation between tickets / dashboard /
  // settings — otherwise the user would get a fresh batch of beeps on
  // every navigation. The hook itself is opt-in via Settings.
  // Realtime subscription to `notifications` table — every INSERT (or
  // mark-as-read UPDATE) on a row addressed to the signed-in user
  // invalidates the bell + unread-count cache within ~200ms. Without
  // this, the SPA was effectively polling every 60s and agents saw a
  // ~1-min lag between a workflow firing and the bell lighting up.
  // The 25s React Query poll above is now a backstop for when the
  // WebSocket drops silently.
  useNotificationsRealtime();
  // Ask before the tab closes while a ticket action is still waiting for the
  // server (they show their result before it confirms).
  usePendingTicketActionsGuard();
  // "Sending in 5s… Undo" for queued replies / new tickets, on whatever page
  // the person has moved on to (the send itself lives in lib/send-outbox.ts).
  useSendOutboxToasts();
  useDesktopNotifications();
  // Tab title + favicon badge — `(3) PA Support Tickets` + red-dot
  // overlay on the favicon whenever the bell unread count is > 0.
  // Cheap, always-on, no opt-in needed. Same mount-once reasoning as
  // the desktop notifications hook above.
  useTabAttention();
  useKeyboardShortcuts([
    {
      key: 'k',
      ctrl: true,
      allowInInput: true,
      handler: (e) => {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      },
    },
    {
      key: '?',
      shift: true, // `?` is Shift+/ on US layouts; the listener checks the resolved char
      handler: (e) => {
        e.preventDefault();
        setCheatsheetOpen(true);
      },
    },
  ]);

  // CustomEvent bridge — the search pill in TopNav (and anywhere else
  // that wants to trigger the palette without prop-drilling) dispatches
  // `pa:open-palette` on window. This keeps the palette state local to
  // AuthedLayout while letting any descendant open it. Same shape as
  // window.addEventListener for any browser event; we just chose our
  // own type string. No untrusted-input concern — both producer and
  // consumer are first-party.
  useEffect(() => {
    function onOpen() { setPaletteOpen(true); }
    window.addEventListener('pa:open-palette', onOpen);
    return () => window.removeEventListener('pa:open-palette', onOpen);
  }, []);

  return (
    // WorkspacePresenceProvider opens a single Realtime presence channel
    // shared across every authed surface — one socket for the whole app.
    // Consumed by TicketRow (dashboard dots) + ticket-detail (setMyTicket).
    <WorkspacePresenceProvider>
      {/* Customer Assist widget lives one level above the route Outlet
          so the channel subscription + chat history + desktop
          notifications all survive navigation. Mounted once per
          AuthedLayout lifetime; no state churn when the agent moves
          between tickets / dashboard / settings. */}
      <AssistSessionProvider>
        {/*
          Single Radix TooltipProvider wraps every authed surface so
          all <ShortcutTooltip> instances share a delay/state pool.
          - delayDuration=300 preserves the original CSS-only feel
            (no instant-flash tooltips when the cursor brushes past).
          - skipDelayDuration=100 means once one tooltip has shown,
            moving to a neighbouring shortcut button feels snappy
            instead of waiting the full 300ms again.
          - disableHoverableContent: tooltips here are read-only —
            never want the user to need to enter the tooltip itself
            to interact with it, which avoids a flicker bug when the
            tooltip overlaps the trigger after a collision flip.
          See components/ShortcutTooltip.tsx for the consumer side.
        */}
        <TooltipPrimitive.Provider
          delayDuration={300}
          skipDelayDuration={100}
          disableHoverableContent
        >
        <PullToRefresh>
          {/* overflow-x-clip (NOT hidden) is a safety net for mobile —
              any single wide element (long email address, oversized
              HTML email table, etc.) gets clipped here so the PAGE
              never horizontally scrolls.
              CRITICAL: must use `clip`, not `hidden`. `overflow-x: hidden`
              cascades to `overflow-y: auto` per CSS Overflow Level 3,
              which makes this div a scroll container and breaks
              `position: sticky` on descendants (the ticket-detail
              right rail, TicketStickyBar, etc. all stopped floating
              when this was `overflow-x-hidden`). `overflow: clip` clips
              without establishing a scroll container, so sticky stays
              attached to the html/body scroll where it belongs. */}
          <div className="flex min-h-screen flex-col overflow-x-clip">
            {/* The nav presents ~15 focusable controls before page content, and
                RouteFadeIn re-mounts the subtree on every navigation — so a
                keyboard user re-tabbed the entire header on each route change.
                Styling lives in index.css (`.skip-link`). */}
            <a href="#main" className="skip-link">
              Skip to main content
            </a>
            <TopNav />
            {/* Nudges each person to enable desktop notifications (browsers need a
                click, so it can't be on by default). Desktop widths only. */}
            <DesktopNotificationsBanner />
            <main
              id="main"
              tabIndex={-1}
              className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-5 focus:outline-none"
            >
              {/* Boundary sits INSIDE Suspense so a lazy chunk that fails to
                  load (stale deploy → 404 on a hashed chunk) renders a recovery
                  card instead of a white screen. Keyed on pathname so
                  navigating away from a broken route clears the error. */}
              <Suspense fallback={<RouteFallback />}>
                <ErrorBoundary key={layoutPathname}>
                  <RouteFadeIn>
                    <Outlet />
                  </RouteFadeIn>
                </ErrorBoundary>
              </Suspense>
            </main>
            <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
            <ShortcutsCheatsheet open={cheatsheetOpen} onOpenChange={setCheatsheetOpen} />
            {/* Floating Customer Assist widget — fixed-positioned, renders
                nothing when phase='idle'. See assist-session-context.tsx +
                CustomerAssistWidget.tsx. */}
            <CustomerAssistWidget />
          </div>
        </PullToRefresh>
        </TooltipPrimitive.Provider>
      </AssistSessionProvider>
    </WorkspacePresenceProvider>
  );
}

export default function App() {
  return (
    // LightboxProvider sits OUTSIDE the auth provider so anonymous
    // routes (/assist/:token, /login, etc.) can also pop images full-
    // screen — the assist client page lets the customer share screenshots
    // and they should be viewable in-place without leaving the page.
    <LightboxProvider>
    <AuthProvider>
      {/*
        Sonner config — Linear/Vercel-style toasts.
        - top-right: moved out of the bottom-right so the floating
          Customer Assist widget + minimized pill aren't covered.
        - offset/mobileOffset top 60: starts the stack below the sticky
          TopNav (44px + border) so toasts never sit on the bell / avatar.
          Every side is set because sonner fills a missing one with its
          own default (32px desktop). The bell's panel opens in the same
          corner and layers above toasts (see NotificationsBell).
        - expand={false}: compact stack until hovered
        - closeButton: explicit dismiss for stickier toasts
        - richColors=false + custom classNames so toasts inherit the app's
          card aesthetic (border, shadow, rounded) instead of sonner's loud
          defaults. Semantic colors come from the icon + the left accent
          stripe, driven by --toast-accent in toastOptions.classNames.
        - the stripe is a background-image layer, NOT ::before/::after:
          sonner owns both pseudo-elements (swipe hit-area, hover gap) and
          scales/moves them on pointerdown and dismiss, which made a
          ::before stripe jump when a toast was clicked. The bg-* utilities
          carry `!` because sonner's own unlayered `background:` shorthand
          otherwise beats Tailwind's layered utilities and resets the image.
      */}
      <Toaster
        position="top-right"
        expand={false}
        closeButton
        visibleToasts={4}
        offset={{ top: 60, right: 16, bottom: 16, left: 16 }}
        mobileOffset={{ top: 60, right: 16, bottom: 16, left: 16 }}
        toastOptions={{
          duration: 3500,
          classNames: {
            toast:
              'group/toast relative flex items-start gap-3 rounded-lg border border-border/70 bg-background/95 p-3.5 pl-4 shadow-md backdrop-blur supports-[backdrop-filter]:bg-background/85 !bg-no-repeat !bg-[image:linear-gradient(var(--toast-accent,transparent),var(--toast-accent,transparent))] !bg-[length:2px_calc(100%-1rem)] !bg-[position:0_50%] [&_[data-icon]>svg]:h-4 [&_[data-icon]>svg]:w-4',
            title: 'text-sm font-semibold tracking-tight text-foreground leading-snug',
            description: 'mt-0.5 text-xs leading-snug text-muted-foreground',
            actionButton:
              'inline-flex h-7 items-center rounded-md bg-foreground px-2.5 text-xs font-medium text-background hover:bg-foreground/90',
            cancelButton:
              'inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs text-muted-foreground hover:text-foreground',
            closeButton:
              '!left-auto !right-2 !top-2 !h-5 !w-5 !rounded !border-0 !bg-transparent text-muted-foreground hover:!bg-accent hover:text-foreground group-data-[dismissible=false]/toast:!hidden group-data-[type=loading]/toast:!hidden',
            success:
              '[&>[data-icon]]:text-tint-success-fg [--toast-accent:var(--color-tint-success-fg)]',
            error:
              '[&>[data-icon]]:text-tint-critical-fg [--toast-accent:var(--color-tint-critical-fg)]',
            warning:
              '[&>[data-icon]]:text-tint-warning-fg [--toast-accent:var(--color-tint-warning-fg)]',
            info:
              '[&>[data-icon]]:text-tint-info-fg [--toast-accent:var(--color-tint-info-fg)]',
            loading:
              '[&>[data-icon]]:text-muted-foreground [--toast-accent:color-mix(in_oklab,var(--color-muted-foreground)_40%,transparent)]',
          },
        }}
      />
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/auth/callback" element={<AuthCallbackRoute />} />
        {/* Customer Assist client surface — anon. The /assist/<token>
            page is the only customer-facing route in the SPA. Sits
            outside RequireAuth like /login. RLS gates the actual data
            access via the x-assist-token header (migration 047). */}
        <Route path="/assist/:token" element={<AssistRoute />} />

        {/* Print-ready client journey: signed-in only, but no nav/layout chrome. */}
        <Route
          path="/journeys/:id/print"
          element={
            <RequireAuth>
              <Suspense fallback={<RouteFallback />}>
                <JourneyPrintRoute />
              </Suspense>
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AuthedLayout />
            </RequireAuth>
          }
        >
          {/* Index lands the user inside their last-visited / default inbox. */}
          <Route index element={<DefaultInboxRedirect />} />

          {/* Inbox-scoped routes — everything under /inbox/:slug runs through
              InboxScopeProvider which resolves :slug → inbox row and exposes
              currentInbox to descendants. Routes read currentInbox and
              filter their queries by inbox_id. */}
          <Route path="/inbox/:slug" element={<InboxScopeProvider><Outlet /></InboxScopeProvider>}>
            <Route index element={<Navigate to="my-day" replace />} />
            <Route path="my-day" element={<MyDayRoute />} />
            <Route path="activity" element={<ActivityRoute />} />
            <Route path="dashboard" element={<DashboardRoute />} />
            <Route path="contacts" element={<ContactsRoute />} />
            <Route path="contacts/view" element={<ContactRoute />} />
          </Route>

          {/* Legacy unscoped URLs redirect to the user's default inbox so
              old bookmarks, /my-day chrome links, etc. keep working. */}
          <Route path="/my-day" element={<DefaultInboxRedirect subPath="my-day" />} />
          <Route path="/activity" element={<DefaultInboxRedirect subPath="activity" />} />
          <Route path="/dashboard" element={<DefaultInboxRedirect subPath="dashboard" />} />
          <Route path="/contacts" element={<DefaultInboxRedirect subPath="contacts" />} />
          <Route path="/contacts/view" element={<DefaultInboxRedirect subPath="contacts/view" />} />

          {/* Ticket detail stays unscoped — the ticket itself carries
              inbox_id and RLS gates access. URL doesn't need a slug. */}
          <Route path="/tickets/:id" element={<TicketDetailRoute />} />
          <Route path="/journeys" element={<JourneysRoute />} />
          <Route path="/kb" element={<KbRoute />} />
          <Route path="/sla-rules" element={<SlaRulesRoute />} />
          <Route path="/team" element={<TeamRoute />} />
          <Route path="/teams" element={<TeamsRoute />} />
          <Route path="/roles" element={<RolesRoute />} />
          <Route path="/access" element={<AccessRoute />} />
          <Route path="/changelog" element={<ChangelogRoute />} />
          <Route path="/notifications" element={<NotificationsRoute />} />
          <Route path="/reports" element={<ReportsRoute />} />
          <Route path="/templates" element={<TemplatesRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/company" element={<CompanyRoute />} />
          <Route path="/leave" element={<LeaveRoute />} />
          <Route path="/inboxes" element={<InboxesRoute />} />
          <Route path="/automations" element={<AutomationsRoute />} />
        </Route>

        <Route path="*" element={<Navigate to="/my-day" replace />} />
      </Routes>
    </AuthProvider>
    </LightboxProvider>
  );
}
